import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { plural, pluralize } from '@alkor/utils';
import { CategoryIcon, IconSearch } from '@/components/icons';
import { Rail, RailSkeleton, CategoriesSkeleton, ErrorState } from '@/components/ui';
import { isNetworkError, isPaused } from '@/lib/online';

const BOT = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? 'alkor_shopbot';

export function HomeScreen() {
  const navigate = useNavigate();
  const { data, isLoading, isPending, fetchStatus, isError, error, refetch } = useQuery({
    queryKey: ['home'],
    queryFn: api.home,
    staleTime: 60_000,
  });

  useEffect(() => { track('miniapp_open'); }, []);

  // Без связи запрос стоит на паузе и ошибки не будет — показываем это сразу,
  // иначе экран бесконечно висит на скелетоне
  if (isPaused({ isPending, fetchStatus })) {
    return <ErrorState offline onRetry={() => void refetch()} />;
  }

  // Скелетон повторяет будущую раскладку экрана, поэтому при появлении данных
  // ничего не «прыгает»: сначала категории, затем две горизонтальные ленты.
  if (isLoading) {
    return (
      <>
        <div className="searchbar" aria-hidden="true"><IconSearch size={17} /><span>Поиск по каталогу</span></div>
        <CategoriesSkeleton />
        <RailSkeleton title="Хиты продаж" />
        <RailSkeleton title="Акции" />
      </>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={(error as Error).message}
        offline={isNetworkError(error)}
        onRetry={() => void refetch()}
      />
    );
  }

  const { categories, popular, promo, newArrivals, recentlyViewed } = data!;
  const total = categories.reduce((n, c) => n + c.productsCount, 0);
  const inStock = categories.reduce((n, c) => n + c.inStockCount, 0);

  return (
    <>
      <section className="x-hero">
        <span className="x-hero__flag">Каталог · Россия</span>
        <h1>Техника и снаряжение<em>для работы в поле</em></h1>
        <p>Гражданские дроны, питание, связь и экипировка. Цены и наличие — из каталога магазина.</p>
        <div className="x-stats">
          <div className="x-stat"><b>{total}</b><small>{plural(total, 'товар', 'товара', 'товаров')} в каталоге</small></div>
          <div className="x-stat"><b>{categories.length}</b><small>{plural(categories.length, 'раздел', 'раздела', 'разделов')}</small></div>
          <div className="x-stat"><b>{inStock}</b><small>в наличии</small></div>
        </div>
      </section>

      <button type="button" className="searchbar" onClick={() => navigate('/search')}>
        <IconSearch size={17} />
        <span>Поиск по каталогу</span>
      </button>

      <div className="block">
        <div className="block__head"><h3>Категории</h3></div>
        <div className="cats">
          {categories.map((c, i) => (
            <button
              key={c.id}
              type="button"
              className={c.imageUrl ? 'cat cat--photo' : 'cat'}
              onClick={() => {
                track('category_view', { categoryId: c.id, props: { slug: c.slug } });
                navigate(`/c/${c.slug}`);
              }}
            >
              {/* Фон — фото товара из раздела. Картинка идёт отдельным слоем,
                  а не фоном CSS: так браузер грузит её лениво и не тормозит экран. */}
              {c.imageUrl && (
                <img className="cat__bg" src={c.imageUrl} alt="" loading="lazy" aria-hidden="true" />
              )}
              <span className="cat__num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              {!c.imageUrl && <CategoryIcon name={c.icon} />}
              <b>{c.name}</b>
              <span>
                {pluralize(c.productsCount, 'товар', 'товара', 'товаров')}
                <br />
                {c.inStockCount} в наличии
              </span>
              <i className="cat__go" aria-hidden="true">→</i>
            </button>
          ))}
        </div>
      </div>

      <Rail title="Хиты продаж" items={popular} />
      {/* Акции — как на сайте. Пока меток нет, на их месте остаются новинки. */}
      <Rail
        title="Акции"
        items={promo ?? []}
        action={
          <button type="button" className="block__more" onClick={() => navigate('/sale')}>
            Все акции →
          </button>
        }
      />
      {(promo?.length ?? 0) === 0 && <Rail title="Новинки" items={newArrivals} />}
      {/* Лента появляется только при наличии истории — пустых заглушек нет */}
      <Rail title="Недавно смотрели" items={recentlyViewed} />

      <div className="foot"><span>ALKOR Tactical</span><span>@{BOT}</span></div>
    </>
  );
}
